import React, { useState } from "react";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import SearchableSelect from "../../../../components/searchableSelect/SearchableSelect";
import TicketCategoryPicker from "../../../../components/ticketCategory/TicketCategoryPicker";
import {
  TICKET_IMPACT_LABELS,
  TICKET_URGENCY_LABELS,
  TicketCategory,
  TicketImpact,
  TicketQueue,
  TicketUrgency,
} from "../../../../interfaces/Ticket";
import AnexosInput from "../../../../components/anexos/AnexosInput";
import { mensagemDeErro } from "../../../../components/anexos/anexosRegras";
import { createTicket, uploadTicketAttachments } from "../../../../services/ticketApi";

interface ClientOption {
  id: number;
  name: string;
}

interface Props {
  open: boolean;
  clients: ClientOption[];
  queues: TicketQueue[];
  categories: TicketCategory[];
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Abertura de chamado EM NOME de um cliente — uso interno (telefone, e-mail avulso, etc.).
 * O cliente abre o dele pelo Portal; esta tela é a exceção operacional, não a via principal.
 *
 * Impacto e urgência são os campos que o cliente responderia — a prioridade é derivada no servidor
 * (design D7). Não há campo de prioridade aqui de propósito: se existisse, quem abre digitaria
 * "urgente" por reflexo e o SLA perderia sentido.
 */
const NewTicketForm: React.FC<Omit<Props, "open">> = ({ clients, queues, categories, onClose, onCreated }) => {
  const [clientId, setClientId] = useState<number | null>(null);
  const [queueId, setQueueId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<TicketImpact>(TicketImpact.UmaPessoa);
  const [urgency, setUrgency] = useState<TicketUrgency>(TicketUrgency.PodeEsperar);
  const [arquivos, setArquivos] = useState<File[]>([]);
  // Preenchido quando o chamado já foi criado mas os arquivos não subiram: "tentar de novo" só
  // reenvia os arquivos, sem abrir um segundo chamado.
  const [criadoId, setCriadoId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Com o chamado já criado, só os arquivos podem mudar: trocar cliente/assunto aqui subiria o
  // arquivo no chamado de outro cliente (público).
  const bloqueado = criadoId != null;

  const reset = () => {
    setClientId(null);
    setQueueId(null);
    setCategoryId(null);
    setSubject("");
    setDescription("");
    setImpact(TicketImpact.UmaPessoa);
    setUrgency(TicketUrgency.PodeEsperar);
    setArquivos([]);
    setCriadoId(null);
  };

  const handleSave = async () => {
    if (clientId == null || !subject.trim() || !description.trim()) return;
    setSaving(true);
    let id = criadoId;
    try {
      if (id == null) {
        const criado = await createTicket({
          clientId,
          queueId,
          categoryId,
          subject: subject.trim(),
          description: description.trim(),
          impact,
          urgency,
        });
        id = criado.id;
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Não foi possível abrir o chamado.");
      setSaving(false);
      return;
    }

    try {
      if (arquivos.length > 0) await uploadTicketAttachments(id, arquivos);
      toast.success("Chamado aberto.");
      reset();
      onCreated();
      onClose();
    } catch (e: any) {
      setCriadoId(id);
      onCreated(); // o chamado existe: a lista precisa mostrá-lo
      const motivo = mensagemDeErro(e, "");
      toast.error(
        `Chamado aberto, mas os arquivos não subiram.${motivo ? ` ${motivo}` : ""} Use "Tentar enviar de novo".`
      );
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";
  const labelCls = "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

  return (
    <Modal onClose={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Abrir chamado</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-1 text-gray-700 transition-colors hover:bg-gray-100 hover:text-navy-700 dark:text-gray-300 dark:hover:bg-navy-700 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <label className={labelCls}>
              Cliente <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              placeholder="Selecione o cliente…"
              value={clientId}
              onChange={(v) => setClientId(v == null ? null : Number(v))}
              disabled={bloqueado}
              options={clients.map((c) => ({ label: c.name, value: c.id }))}
            />
          </div>

          <div>
            <label className={labelCls}>Categoria</label>
            <TicketCategoryPicker
              categories={categories}
              value={categoryId}
              onChange={setCategoryId}
              disabled={bloqueado}
              hint="Do que se trata. Com a fila em branco, a categoria decide para qual time o chamado vai."
            />
          </div>

          <div>
            <label className={labelCls}>Fila</label>
            <SearchableSelect
              placeholder="— Pela categoria —"
              value={queueId}
              onChange={(v) => setQueueId(v == null ? null : Number(v))}
              disabled={bloqueado}
              options={queues.map((q) => ({ label: q.name, value: q.id }))}
            />
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Escolher uma fila aqui ganha do roteamento da categoria.
            </p>
          </div>

          <div>
            <label htmlFor="ticket-subject" className={labelCls}>
              Assunto <span className="text-red-500">*</span>
            </label>
            <input
              id="ticket-subject"
              className={inputCls}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              readOnly={bloqueado}
              placeholder="Ex.: Notebook não liga"
            />
          </div>

          <div>
            <label htmlFor="ticket-description" className={labelCls}>
              Descrição <span className="text-red-500">*</span>
            </label>
            <AnexosInput value={arquivos} onChange={setArquivos} disabled={saving}>
              <textarea
                id="ticket-description"
                className={inputCls}
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                readOnly={bloqueado}
                placeholder="O que está acontecendo…"
              />
            </AnexosInput>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ticket-impact" className={labelCls}>
                Impacto
              </label>
              <select
                id="ticket-impact"
                className={inputCls}
                value={impact}
                onChange={(e) => setImpact(Number(e.target.value))}
                disabled={bloqueado}
              >
                {Object.entries(TICKET_IMPACT_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ticket-urgency" className={labelCls}>
                Urgência
              </label>
              <select
                id="ticket-urgency"
                className={inputCls}
                value={urgency}
                onChange={(e) => setUrgency(Number(e.target.value))}
                disabled={bloqueado}
              >
                {Object.entries(TICKET_URGENCY_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="-mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            A prioridade é calculada a partir de impacto e urgência ao salvar.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || clientId == null || !subject.trim() || !description.trim() || (criadoId != null && arquivos.length === 0)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-6 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            {saving ? "Enviando…" : criadoId != null ? "Tentar enviar de novo" : "Abrir chamado"}
          </button>
        </div>
      </div>
    </Modal>
  );
};


/**
 * O formulário é DESMONTADO quando fechado: todo o estado (inclusive `criadoId`) morre junto.
 * Guardar o estado no wrapper fechado fazia "tentar de novo" subir arquivo do cliente B no
 * chamado do cliente A depois de fechar e reabrir.
 */
const NewTicketModal: React.FC<Props> = ({ open, ...resto }) =>
  open ? <NewTicketForm {...resto} /> : null;

export default NewTicketModal;
